// ============================================
// src/PaystackPayment.js - Paystack Integration (NGN)
// Replaces FlutterwavePayment.js
// ============================================
//import React from 'react';
//import { CreditCard, Check, Crown, X } from 'lucide-react';
//import { SUBSCRIPTION_PLANS } from './SubscriptionSystem';
import { USD_TO_NGN } from './CoinSystem';

/// ============================================
// src/PaystackPayment.js
// ONLY exports the checkout trigger function
// SubscriptionModal now lives in PaymentSystem.js
// ============================================

const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_KEY;

const convertUsdCentsToNgnKobo = (usdCents) => {
  const usdDollars = usdCents / 100;
  const ngnNaira = usdDollars * USD_TO_NGN;
  return Math.round(ngnNaira * 100);
};

const isPaystackLoaded = () => {
  return typeof window !== 'undefined' && window.PaystackPop !== undefined;
};

export const initiatePaystackPayment = (email, usdCents, plan, userName, onSuccess, onClose) => {
  if (isPaystackLoaded()) {
    openPaystackModal(email, usdCents, plan, userName, onSuccess, onClose);
    return;
  }

  const existingScript = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');

  if (existingScript) {
    existingScript.onload = () => {
      openPaystackModal(email, usdCents, plan, userName, onSuccess, onClose);
    };
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://js.paystack.co/v1/inline.js';
  script.async = true;

  script.onload = () => {
    console.log('Paystack script loaded successfully');
    openPaystackModal(email, usdCents, plan, userName, onSuccess, onClose);
  };

  script.onerror = (error) => {
    console.error('Failed to load Paystack script:', error);
    alert('Failed to load payment system. Please check your internet connection and try again.');
    onClose();
  };

  document.body.appendChild(script);
};

const openPaystackModal = (email, usdCents, plan, userName, onSuccess, onClose) => {
  try {
    if (!window.PaystackPop) {
      throw new Error('Paystack not loaded');
    }

    if (!PAYSTACK_PUBLIC_KEY) {
      throw new Error('Paystack public key not configured. Set REACT_APP_PAYSTACK_KEY in your .env file.');
    }

    const ngnKobo = convertUsdCentsToNgnKobo(usdCents);
    const reference = 'SUB_' + Date.now() + '_' + Math.floor(Math.random() * 1000000);

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: email || 'user@example.com',
      amount: ngnKobo,
      currency: 'NGN',
      ref: reference,
      metadata: {
        subscription_plan: plan,
        custom_fields: [
          {
            display_name: 'Customer Name',
            variable_name: 'customer_name',
            value: userName || 'Subscriber'
          },
          {
            display_name: 'USD Amount',
            variable_name: 'usd_amount',
            value: (usdCents / 100).toFixed(2)
          }
        ]
      },
      callback: function(response) {
        console.log('Payment successful:', response);
        if (response.status === 'success' || response.reference) {
          onSuccess({
            transaction_id: response.reference,
            tx_ref: response.reference,
            status: 'successful',
            raw: response
          });
        } else {
          alert('Payment verification failed. Please contact support.');
          onClose();
        }
      },
      onClose: function() {
        console.log('Payment modal closed');
        onClose();
      }
    });

    handler.openIframe();
  } catch (error) {
    console.error('Error opening Paystack modal:', error);
    alert('Error: ' + error.message);
    onClose();
  }
};

// ============================================
// ADD THIS FUNCTION to src/PaystackPayment.js
// (keep everything already in the file, just add this export)
//
// This is a NAIRA-NATIVE version for coin purchases.
// Coins are already priced in Naira, so NO USD->NGN conversion.
// The existing initiatePaystackPayment (for subscriptions) takes USD cents
// and converts. This one takes Naira directly.
// ============================================

export const initiatePaystackPaymentNGN = (email, amountNgn, itemName, userName, onSuccess, onClose) => {
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_KEY;

  const openModal = () => {
    try {
      if (!window.PaystackPop) throw new Error('Paystack not loaded');
      if (!PAYSTACK_PUBLIC_KEY) throw new Error('Paystack public key not configured. Set REACT_APP_PAYSTACK_KEY in .env');

      const ngnKobo = Math.round(amountNgn * 100); // Naira -> kobo
      const reference = 'COINS_' + Date.now() + '_' + Math.floor(Math.random() * 1000000);

      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: email || 'user@example.com',
        amount: ngnKobo,
        currency: 'NGN',
        ref: reference,
        metadata: {
          purchase_type: 'coins',
          item: itemName,
          custom_fields: [
            { display_name: 'Customer', variable_name: 'customer_name', value: userName || 'Buyer' },
            { display_name: 'Item', variable_name: 'item', value: itemName }
          ]
        },
        callback: function(response) {
          if (response.status === 'success' || response.reference) {
            onSuccess({
              transaction_id: response.reference,
              reference: response.reference,
              status: 'successful'
            });
          } else {
            alert('Payment verification failed. Please contact support.');
            onClose();
          }
        },
        onClose: function() {
          onClose();
        }
      });

      handler.openIframe();
    } catch (error) {
      console.error('Error opening Paystack (NGN):', error);
      alert('Error: ' + error.message);
      onClose();
    }
  };

  // Load script if needed, then open
  if (typeof window !== 'undefined' && window.PaystackPop) {
    openModal();
    return;
  }

  const existingScript = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
  if (existingScript) {
    existingScript.onload = openModal;
    if (window.PaystackPop) openModal();
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://js.paystack.co/v1/inline.js';
  script.async = true;
  script.onload = openModal;
  script.onerror = () => {
    alert('Failed to load payment system. Check your internet connection.');
    onClose();
  };
  document.body.appendChild(script);
};